import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import {
    deleteBulkManuscript,
    getLatestManuscriptCompile,
    getLatestManuscriptJob,
    getLatestSuccessfulManuscriptCompile,
    getManuscript,
    getManuscriptDocument,
    getManuscriptHistory,
    getManuscriptList,
    getManuscriptReviews,
    getManuscriptTemplateList,
    getManuscriptTree,
    patchManuscript,
    postManuscript,
    postManuscriptCompile,
    postManuscriptTemplateGeneration,
    postManuscriptHistoryRestore,
    postManuscriptReview,
    postManuscriptReviewMessage,
    postManuscriptReviewReopen,
    postManuscriptReviewResolve,
} from "utils/request/manuscript";

const useCurrentSpaceUuid = () => {
    const match = useMatch("/:space_uuid/*");
    return match?.params?.space_uuid ?? "";
};

export const useManuscripts = (params, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const refetchInterval = options.refetchInterval ?? false;
    return useQuery(["manuscripts", space_uuid, params], () => getManuscriptList(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        keepPreviousData: true,
        refetchInterval,
    });
};

export const useManuscript = (manuscriptId) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["manuscript", space_uuid, manuscriptId], () => getManuscript(space_uuid, manuscriptId), {
        enabled: !!manuscriptId,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useManuscriptTree = (manuscriptId) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["manuscriptTree", space_uuid, manuscriptId], () => getManuscriptTree(space_uuid, manuscriptId), {
        enabled: !!manuscriptId,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useManuscriptDocument = (manuscriptId, nodeId) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(
        ["manuscriptDocument", space_uuid, manuscriptId, nodeId],
        () => getManuscriptDocument(space_uuid, manuscriptId, nodeId),
        {
            enabled: !!manuscriptId && !!nodeId,
            staleTime: Infinity,
            cacheTime: Infinity,
            retry: false,
        },
    );
};

export const useManuscriptTemplates = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["manuscriptTemplates", space_uuid, params], () => getManuscriptTemplateList(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useLatestManuscriptCompile = (manuscriptId, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const refetchInterval = options.refetchInterval ?? false;
    return useQuery(
        ["manuscriptJobLatest", space_uuid, manuscriptId, { job_type: "compile" }],
        () => getLatestManuscriptCompile(space_uuid, manuscriptId),
        {
            enabled: !!manuscriptId && options.enabled !== false,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            retry: false,
            refetchInterval,
        },
    );
};

export const useLatestSuccessfulManuscriptCompile = (manuscriptId, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(
        ["manuscriptJobLatestSuccessful", space_uuid, manuscriptId, { job_type: "compile" }],
        () => getLatestSuccessfulManuscriptCompile(space_uuid, manuscriptId),
        {
            enabled: !!manuscriptId && options.enabled !== false,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            retry: false,
        },
    );
};

export const useLatestManuscriptJob = (manuscriptId, params, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const refetchInterval = options.refetchInterval ?? false;
    return useQuery(
        ["manuscriptJobLatest", space_uuid, manuscriptId, params],
        () => getLatestManuscriptJob(space_uuid, manuscriptId, params),
        {
            enabled: !!manuscriptId && options.enabled !== false,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            retry: false,
            refetchInterval,
        },
    );
};

export const useManuscriptHistory = (manuscriptId, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const refetchInterval = options.refetchInterval ?? false;
    return useQuery(
        ["manuscriptHistory", space_uuid, manuscriptId],
        () => getManuscriptHistory(space_uuid, manuscriptId),
        {
            enabled: !!manuscriptId && options.enabled !== false,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            retry: false,
            refetchInterval,
        },
    );
};

export const useManuscriptReviews = (manuscriptId, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(
        ["manuscriptReviews", space_uuid, manuscriptId],
        () => getManuscriptReviews(space_uuid, manuscriptId),
        {
            enabled: !!manuscriptId && options.enabled !== false,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            retry: false,
        },
    );
};

export const useManuscriptCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => postManuscript(space_uuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "manuscripts" });
        },
    });
};

export const useManuscriptTemplateGenerationCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => postManuscriptTemplateGeneration(space_uuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "manuscripts" });
        },
    });
};

export const useManuscriptUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => patchManuscript(space_uuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "manuscripts" });
        },
    });
};

export const useManuscriptBulkDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => deleteBulkManuscript(space_uuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "manuscripts" });
        },
    });
};

export const useManuscriptCompileCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((manuscriptId) => postManuscriptCompile(space_uuid, manuscriptId), {
        onSuccess: (_, manuscriptId) => {
            queryClient.invalidateQueries(["manuscriptJobLatest", space_uuid, manuscriptId]);
            queryClient.invalidateQueries(["manuscriptJobLatestSuccessful", space_uuid, manuscriptId]);
            queryClient.invalidateQueries(["manuscriptJobLatest", space_uuid, manuscriptId]);
            queryClient.invalidateQueries(["manuscriptHistory", space_uuid, manuscriptId]);
        },
    });
};

export const useManuscriptHistoryRestore = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation(
        ({ manuscriptId, revisionId }) => postManuscriptHistoryRestore(space_uuid, manuscriptId, revisionId),
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(["manuscriptHistory", space_uuid, variables.manuscriptId]);
                queryClient.invalidateQueries(["manuscriptTree", space_uuid, variables.manuscriptId]);
                queryClient.invalidateQueries(["manuscript", space_uuid, variables.manuscriptId]);
            },
        },
    );
};

export const useManuscriptReviewCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation(({ manuscriptId, ...body }) => postManuscriptReview(space_uuid, manuscriptId, body), {
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries(["manuscriptReviews", space_uuid, variables.manuscriptId]);
        },
    });
};

export const useManuscriptReviewMessageCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation(
        ({ manuscriptId, threadId, content }) =>
            postManuscriptReviewMessage(space_uuid, manuscriptId, threadId, { content }),
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(["manuscriptReviews", space_uuid, variables.manuscriptId]);
            },
        },
    );
};

export const useManuscriptReviewResolve = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation(
        ({ manuscriptId, threadId }) => postManuscriptReviewResolve(space_uuid, manuscriptId, threadId),
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(["manuscriptReviews", space_uuid, variables.manuscriptId]);
            },
        },
    );
};

export const useManuscriptReviewReopen = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation(({ manuscriptId, threadId }) => postManuscriptReviewReopen(space_uuid, manuscriptId, threadId), {
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries(["manuscriptReviews", space_uuid, variables.manuscriptId]);
        },
    });
};
